import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isFinisherOrders, isReviewOrders, onErrorMessage } from '@/helpers'
import { useAuth } from '@/providers'

import { QUERIES_ADMIN, useOrderCenterSaleOrderUpdate } from '@/services'
import { SaleOrderProps } from '@/types'
import { useQueryClient } from '@tanstack/react-query'

import { SelectStatus } from '../select-status'

import { ModalCancellationOrder } from './components'

import { saleOrderStatusOptions } from './select-sale-order-status.constants'

import { SelectSaleOrderStatusProps } from './select-sale-order-status.type'

export const SelectSaleOrderStatus = ({
  saleOrder,
  size = 'medium',
}: SelectSaleOrderStatusProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentsSelectOrderStatus')

  const { whoAmI } = useAuth()

  const queryClient = useQueryClient()

  const [isOpenCancellationOrderModal, setIsOpenCancellationOrderModal] =
    useState(false)

  const isReviewOrdersEnabled =
    isReviewOrders(whoAmI?.email) &&
    ['created', 'processing'].includes(saleOrder?.status || '')

  const saleOrderStatusOptionsByRole = saleOrderStatusOptions(
    tEnums,
    isReviewOrdersEnabled,
  )

  const isFinisherOrderEnabled = isFinisherOrders(whoAmI?.email)

  const { mutateAsync, isPending: isLoadingUpdateOrder } =
    useOrderCenterSaleOrderUpdate(saleOrder?.uuid)

  const onUpdate = (success: boolean) => {
    if (success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrder?.uuid],
      })
    } else {
      onErrorMessage(
        t('orderStatusesErrorMessageTitle'),
        t('orderStatusesErrorMessageDescription'),
      )
    }
  }

  const onUpdateOrderStatus = async (status: SaleOrderProps['status']) => {
    const response = await mutateAsync({ status })

    onUpdate(response.success)
  }

  const onCancellationOrder = async (saleOrder: Partial<SaleOrderProps>) => {
    const response = await mutateAsync({ ...saleOrder, status: 'cancelled' })

    return response
  }

  return (
    <div>
      <SelectStatus
        size={size}
        status={saleOrder?.status}
        options={saleOrderStatusOptionsByRole}
        {...((isFinisherOrderEnabled || isReviewOrdersEnabled) && {
          onChange: async (status) => {
            if (status === 'cancelled')
              return setIsOpenCancellationOrderModal(true)

            await onUpdateOrderStatus(status as SaleOrderProps['status'])
          },
        })}
      />
      <ModalCancellationOrder
        isLoading={isLoadingUpdateOrder}
        isOpen={isOpenCancellationOrderModal}
        onClose={() => setIsOpenCancellationOrderModal(false)}
        saleOrder={saleOrder}
        onConfirm={async (saleOrder) => await onCancellationOrder(saleOrder)}
      />
    </div>
  )
}
